import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { useCamions, useCreateBonTravail, useUpdateBonTravail } from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import {
  CAUSE_BT_LABELS, LIEU_BT_LABELS, NIVEAU_BT_LABELS, PRIORITE_BT_LABELS,
  STATUT_BT_LABELS, TYPE_BT_LABELS,
  type BonTravail, type CauseBonTravail, type LieuBonTravail,
  type NiveauBonTravail, type PrioriteBonTravail, type StatutBonTravail, type TypeBonTravail,
} from '@/types';

interface Props {
  open:    boolean;
  onClose: () => void;
  bt?:     BonTravail; // undefined = création
}

interface LigneState {
  designation:      string;
  reference:        string;
  qte:              string;
  prixUnitaireFcfa: string;
}

const TAUX_DEFAULT = 3000; // FCFA/h (configurable plus tard)

const fmtInt = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
    .format(n)
    .replace(/[  ]/g, ' ');

/**
 * Brief §4.3 — Formulaire BT en 6 sections.
 */
export const BonTravailDialog = ({ open, onClose, bt }: Props) => {
  const user = useAuthStore((s) => s.user);
  const isEdit = !!bt;
  const create = useCreateBonTravail();
  const update = useUpdateBonTravail();
  const { data: camionsData } = useCamions({ actif: true, limit: 500 });
  const camions = camionsData?.data ?? [];

  // 1 — Identification
  const [camionId, setCamionId]   = useState('');
  const [signalePar, setSignalePar] = useState('');
  // 2 — Nature
  const [type, setType]                       = useState<TypeBonTravail>('curatif');
  const [niveau, setNiveau]                   = useState<NiveauBonTravail>('n3_10000km');
  const [lieu, setLieu]                       = useState<LieuBonTravail>('garage_central');
  const [descriptionPanne, setDescriptionPanne] = useState('');
  const [cause, setCause]                     = useState<CauseBonTravail | ''>('');
  // 3 — Assignation
  const [mecanicien, setMecanicien]           = useState('');
  const [mecaniciensAdd, setMecaniciensAdd]   = useState('');
  const [chefParc, setChefParc]               = useState('');
  const [priorite, setPriorite]               = useState<PrioriteBonTravail>('normale');
  // 4 — Pièces (lignes dynamiques)
  const [lignes, setLignes] = useState<LigneState[]>([]);
  // 5 — Temps & coûts
  const [heureDebut, setHeureDebut]           = useState('');
  const [heureFin, setHeureFin]               = useState('');
  const [tauxHoraire, setTauxHoraire]         = useState(String(TAUX_DEFAULT));
  const [coutExterne, setCoutExterne]         = useState('');
  // 6 — Clôture (uniquement en édition)
  const [travauxEffectues, setTravauxEffectues]   = useState('');
  const [testEffectue, setTestEffectue]           = useState(false);
  const [testDescription, setTestDescription]     = useState('');
  const [remiseEnService, setRemiseEnService]     = useState('');
  const [kmCloture, setKmCloture]                 = useState('');
  const [signMeca, setSignMeca]                   = useState(false);
  const [signChefParc, setSignChefParc]           = useState(false);
  const [statut, setStatut]                       = useState<StatutBonTravail>('ouvert');
  const [notes, setNotes]                         = useState('');
  const [error, setError]                         = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (bt) {
      setCamionId(bt.camionId);
      setSignalePar(bt.signaleParNom ?? '');
      setType(bt.type); setNiveau(bt.niveau); setLieu(bt.lieu);
      setDescriptionPanne(bt.descriptionPanne);
      setCause(bt.cause ?? '');
      setMecanicien(bt.mecanicienPrincipalNom);
      setMecaniciensAdd((bt.mecaniciensAdditionnels ?? []).join(', '));
      setChefParc(bt.chefParcNom ?? '');
      setPriorite(bt.priorite);
      setLignes((bt.lignes ?? []).map((l) => ({
        designation:      l.designation,
        reference:        l.reference ?? '',
        qte:              l.qte,
        prixUnitaireFcfa: l.prixUnitaireFcfa,
      })));
      setHeureDebut(bt.heureDebut ? bt.heureDebut.slice(0, 16) : '');
      setHeureFin(bt.heureFin     ? bt.heureFin.slice(0, 16)   : '');
      setTauxHoraire(bt.tauxHoraireFcfa ?? String(TAUX_DEFAULT));
      setCoutExterne(bt.coutExterneFcfa ?? '');
      setTravauxEffectues(bt.travauxEffectues ?? '');
      setTestEffectue(bt.testEffectue);
      setTestDescription(bt.testDescription ?? '');
      setRemiseEnService(bt.remiseEnServiceAt ? bt.remiseEnServiceAt.slice(0, 16) : '');
      setKmCloture(bt.kilometrageClotureKm?.toString() ?? '');
      setSignMeca(bt.signedByMecanicien);
      setSignChefParc(bt.signedByChefParc);
      setStatut(bt.statut);
      setNotes(bt.notes ?? '');
    } else {
      setCamionId(''); setSignalePar(user?.fullName ?? '');
      setType('curatif'); setNiveau('n3_10000km'); setLieu('garage_central');
      setDescriptionPanne(''); setCause('');
      setMecanicien(''); setMecaniciensAdd(''); setChefParc(''); setPriorite('normale');
      setLignes([]);
      setHeureDebut(''); setHeureFin('');
      setTauxHoraire(String(TAUX_DEFAULT)); setCoutExterne('');
      setTravauxEffectues(''); setTestEffectue(false); setTestDescription('');
      setRemiseEnService(''); setKmCloture('');
      setSignMeca(false); setSignChefParc(false);
      setStatut('ouvert'); setNotes('');
    }
    setError(null);
  }, [open, bt, user]);

  // Camion sélectionné (info rapide)
  const camion = useMemo(() => camions.find((c) => c.id === camionId), [camions, camionId]);

  // Calculs coûts live
  const calc = useMemo(() => {
    let tempsMin: number | null = null;
    let coutMain = 0;
    if (heureDebut && heureFin) {
      const ms = new Date(heureFin).getTime() - new Date(heureDebut).getTime();
      if (ms > 0) {
        tempsMin = Math.floor(ms / 60_000);
        coutMain = (tempsMin / 60) * (parseFloat(tauxHoraire) || 0);
      }
    }
    const coutPieces = lignes.reduce((s, l) => s + (parseFloat(l.qte) || 0) * (parseFloat(l.prixUnitaireFcfa) || 0), 0);
    const coutExt    = parseFloat(coutExterne) || 0;
    return {
      tempsMin,
      coutMain,
      coutPieces,
      coutExt,
      total: coutMain + coutPieces + coutExt,
    };
  }, [heureDebut, heureFin, tauxHoraire, lignes, coutExterne]);

  const addLigne = () => setLignes([...lignes, { designation: '', reference: '', qte: '1', prixUnitaireFcfa: '0' }]);
  const removeLigne = (i: number) => setLignes(lignes.filter((_, idx) => idx !== i));
  const updateLigne = (i: number, patch: Partial<LigneState>) => setLignes(lignes.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const requireSignature = statut === 'valide' && (!signMeca || !signChefParc);
  const canSubmit = camionId && mecanicien.trim() && descriptionPanne.trim() && !requireSignature;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const lignesPayload = lignes
      .filter((l) => l.designation.trim() && parseFloat(l.qte) > 0)
      .map((l) => ({
        designation:      l.designation.trim(),
        reference:        l.reference.trim() || undefined,
        qte:              parseFloat(l.qte),
        prixUnitaireFcfa: parseFloat(l.prixUnitaireFcfa) || 0,
      }));
    const basePayload = {
      camionId,
      signaleParNom:          signalePar.trim() || undefined,
      type, niveau, lieu,
      descriptionPanne:       descriptionPanne.trim(),
      cause:                  cause || undefined,
      mecanicienPrincipalNom: mecanicien.trim(),
      mecaniciensAdditionnels: mecaniciensAdd.split(',').map((s) => s.trim()).filter(Boolean),
      chefParcNom:            chefParc.trim() || undefined,
      priorite,
      lignes:                 lignesPayload,
      heureDebut:             heureDebut ? new Date(heureDebut).toISOString() : undefined,
      heureFin:               heureFin   ? new Date(heureFin).toISOString()   : undefined,
      tauxHoraireFcfa:        parseFloat(tauxHoraire) || undefined,
      coutExterneFcfa:        coutExterne ? parseFloat(coutExterne) : undefined,
      notes:                  notes.trim() || undefined,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({
          id: bt!.id,
          payload: {
            ...basePayload,
            travauxEffectues:     travauxEffectues.trim() || undefined,
            testEffectue,
            testDescription:      testDescription.trim() || undefined,
            remiseEnServiceAt:    remiseEnService ? new Date(remiseEnService).toISOString() : undefined,
            kilometrageClotureKm: kmCloture ? parseInt(kmCloture, 10) : undefined,
            signedByMecanicien:   signMeca,
            signedByChefParc:     signChefParc,
            statut,
          },
        });
      } else {
        await create.mutateAsync(basePayload);
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Modifier ${bt!.numero}` : 'Créer un Bon de Travail'}
      subtitle={isEdit ? `Statut : ${STATUT_BT_LABELS[bt!.statut]} · Coût : ${fmtInt(parseFloat(bt!.coutTotalFcfa ?? '0'))} FCFA` : 'Brief §4.3 · 6 sections'}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Section title="1 · Identification">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Camion *">
              <select value={camionId} onChange={(e) => setCamionId(e.target.value)} required disabled={isEdit} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Sélectionner —</option>
                {camions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code}{c.immatriculation ? ` · ${c.immatriculation}` : ''}{c.modele ? ` · ${c.modele}` : ''}{c.kmActuel ? ` · ${fmtInt(c.kmActuel)} km` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Signalé par">
              <input value={signalePar} onChange={(e) => setSignalePar(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
          </div>
          {camion && (
            <div style={{ padding: 8, fontSize: 11, background: 'rgba(192,0,0,.04)', border: '1px solid var(--bdr)', borderRadius: 4, color: 'var(--tx-2)' }}>
              ℹ️ <strong>{camion.code}</strong> · {camion.modele ?? ''} · km actuel : {camion.kmActuel ? fmtInt(camion.kmActuel) : '—'} · statut : {camion.statut}
            </div>
          )}
        </Section>

        <Section title="2 · Nature de l'intervention">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Type *">
              <select value={type} onChange={(e) => setType(e.target.value as TypeBonTravail)} className="ds-fi" style={{ width: '100%' }}>
                {(Object.keys(TYPE_BT_LABELS) as TypeBonTravail[]).map((t) => <option key={t} value={t}>{TYPE_BT_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field label="Niveau *">
              <select value={niveau} onChange={(e) => setNiveau(e.target.value as NiveauBonTravail)} className="ds-fi" style={{ width: '100%' }}>
                {(Object.keys(NIVEAU_BT_LABELS) as NiveauBonTravail[]).map((n) => <option key={n} value={n}>{NIVEAU_BT_LABELS[n]}</option>)}
              </select>
            </Field>
            <Field label="Lieu *">
              <select value={lieu} onChange={(e) => setLieu(e.target.value as LieuBonTravail)} className="ds-fi" style={{ width: '100%' }}>
                {(Object.keys(LIEU_BT_LABELS) as LieuBonTravail[]).map((l) => <option key={l} value={l}>{LIEU_BT_LABELS[l]}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description panne *">
            <textarea value={descriptionPanne} onChange={(e) => setDescriptionPanne(e.target.value)} required rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Ex : fumée noire échappement + perte de puissance sur cote" />
          </Field>
          <Field label="Cause identifiée">
            <select value={cause} onChange={(e) => setCause(e.target.value as CauseBonTravail | '')} className="ds-fi" style={{ width: '100%' }}>
              <option value="">— À identifier —</option>
              {(Object.keys(CAUSE_BT_LABELS) as CauseBonTravail[]).map((c) => <option key={c} value={c}>{CAUSE_BT_LABELS[c]}</option>)}
            </select>
          </Field>
        </Section>

        <Section title="3 · Assignation">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mécanicien principal *">
              <input value={mecanicien} onChange={(e) => setMecanicien(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Mécaniciens additionnels (séparés par ,)">
              <input value={mecaniciensAdd} onChange={(e) => setMecaniciensAdd(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Alpha Bah, Sékou Diallo" />
            </Field>
            <Field label="Chef de parc">
              <input value={chefParc} onChange={(e) => setChefParc(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Priorité">
              <select value={priorite} onChange={(e) => setPriorite(e.target.value as PrioriteBonTravail)} className="ds-fi" style={{ width: '100%' }}>
                {(Object.keys(PRIORITE_BT_LABELS) as PrioriteBonTravail[]).map((p) => <option key={p} value={p}>{PRIORITE_BT_LABELS[p]}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        <Section title={`4 · Pièces utilisées${lignes.length > 0 ? ` (${lignes.length})` : ''}`}>
          {lignes.length === 0 ? (
            <div style={{ padding: 8, color: 'var(--tx-3)', fontSize: 12, fontStyle: 'italic' }}>Aucune pièce. Clique « Ajouter une pièce » si nécessaire.</div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {lignes.map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1.5fr 0.8fr 1.2fr 1.2fr auto', gap: 6, alignItems: 'center' }}>
                  <input value={l.designation} onChange={(e) => updateLigne(i, { designation: e.target.value })} className="ds-fi" placeholder="Désignation" />
                  <input value={l.reference}   onChange={(e) => updateLigne(i, { reference: e.target.value })}   className="ds-fi" placeholder="Réf OEM" />
                  <input type="number" min={0} step="0.1" value={l.qte} onChange={(e) => updateLigne(i, { qte: e.target.value })} className="ds-fi" placeholder="Qté" />
                  <input type="number" min={0} value={l.prixUnitaireFcfa} onChange={(e) => updateLigne(i, { prixUnitaireFcfa: e.target.value })} className="ds-fi" placeholder="Prix unit." />
                  <div style={{ fontSize: 11, color: 'var(--p)', fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtInt((parseFloat(l.qte) || 0) * (parseFloat(l.prixUnitaireFcfa) || 0))} F
                  </div>
                  <button type="button" onClick={() => removeLigne(i)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
          <button type="button" onClick={addLigne} className="ds-btn ds-btn-g" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={12} /> Ajouter une pièce
          </button>
        </Section>

        <Section title="5 · Temps & coûts">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Heure début">
              <input type="datetime-local" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Heure fin">
              <input type="datetime-local" value={heureFin} onChange={(e) => setHeureFin(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Taux horaire (FCFA/h)">
              <input type="number" min={0} value={tauxHoraire} onChange={(e) => setTauxHoraire(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Coût externe (FCFA)">
              <input type="number" min={0} value={coutExterne} onChange={(e) => setCoutExterne(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Prestataire externe" />
            </Field>
          </div>
          {/* Synthèse coûts live */}
          <div style={{ padding: 10, background: 'rgba(168,85,247,.06)', borderRadius: 4, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              <Stat label="Temps passé"     value={calc.tempsMin !== null ? `${Math.floor(calc.tempsMin / 60)}h ${calc.tempsMin % 60}m` : '—'} />
              <Stat label="Main d'œuvre"   value={`${fmtInt(calc.coutMain)} F`} />
              <Stat label="Pièces"          value={`${fmtInt(calc.coutPieces)} F`} />
              <Stat label="Externe"         value={`${fmtInt(calc.coutExt)} F`} />
              <Stat label="TOTAL"           value={`${fmtInt(calc.total)} F`} strong />
            </div>
          </div>
        </Section>

        {isEdit && (
          <Section title="6 · Clôture">
            <Field label="Travaux effectués">
              <textarea value={travauxEffectues} onChange={(e) => setTravauxEffectues(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Description détaillée de ce qui a été fait..." />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={testEffectue} onChange={(e) => setTestEffectue(e.target.checked)} />
                Test effectué après réparation
              </label>
              <Field label="Description test">
                <input value={testDescription} onChange={(e) => setTestDescription(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
              <Field label="Remise en service (date/heure)">
                <input type="datetime-local" value={remiseEnService} onChange={(e) => setRemiseEnService(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
              <Field label="Kilométrage à la clôture">
                <input type="number" min={0} value={kmCloture} onChange={(e) => setKmCloture(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: 16, padding: 8, background: '#f9fafb', borderRadius: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={signMeca} onChange={(e) => setSignMeca(e.target.checked)} />
                Signature mécanicien ✓
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={signChefParc} onChange={(e) => setSignChefParc(e.target.checked)} />
                Signature chef de parc ✓
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Statut">
                <select value={statut} onChange={(e) => setStatut(e.target.value as StatutBonTravail)} className="ds-fi" style={{ width: '100%' }}>
                  {(Object.keys(STATUT_BT_LABELS) as StatutBonTravail[]).map((s) => <option key={s} value={s}>{STATUT_BT_LABELS[s]}</option>)}
                </select>
              </Field>
            </div>
            {requireSignature && (
              <div style={{ padding: 6, background: '#fee2e2', color: '#dc2626', borderRadius: 4, fontSize: 11 }}>
                ⚠️ Signatures mécanicien + chef parc requises pour valider le BT
              </div>
            )}
          </Section>
        )}

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
            {create.isPending || update.isPending ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer le BT'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset style={{ border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)', padding: 10 }} className="space-y-2">
    <legend style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 6px' }}>{title}</legend>
    {children}
  </fieldset>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);

const Stat = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div>
    <div style={{ fontSize: 9, color: 'var(--tx-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: strong ? 14 : 12, fontWeight: strong ? 700 : 500, color: strong ? 'var(--p)' : 'var(--tx-1)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);
